import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { CalendarClock, EyeOff, KeyRound, CreditCard, Hourglass } from 'lucide-react-native';
import { ScreenFrame } from '@/components/ScreenFrame';
import { AccountActionButton } from '@/components/account/AccountActionButton';
import { StepUpModal } from '@/components/account/StepUpModal';
import { AuthGuardError } from '@/components/AuthGuardError';
import { useAlert } from '@/components/AlertDialog';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useAuth } from '@/lib/supabase';
import { handleError, reportError } from '@/lib/errors';
import {
  AccountDeletionError,
  StepUpRequiredError,
  daysUntil,
  formatDeletionDate,
  useCancelDeletion,
  useDeletionStatus,
  cancelledMessage,
} from '@/lib/gdpr/account-deletion';

/**
 * The grace-period screen (118-07). Every signed-in route lands here while a
 * deletion request is open (DeletionGate, root layout). There is no back
 * control and no app shell: the only exits are Cancel deletion and Sign out.
 */
export default function AccountDeletionPendingScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { user, signOut } = useAuth();
  const { alert } = useAlert();
  const status = useDeletionStatus(user?.id);
  const cancel = useCancelDeletion(user?.id);
  const [stepUp, setStepUp] = useState<boolean>(false);
  const [signingOut, setSigningOut] = useState<boolean>(false);

  const deletion = status.data;

  // Nothing to show here once there is no open request (cancelled, or arrived
  // by a stale link) or no session: hand back to the root dispatcher.
  useEffect(() => {
    if (!user) router.replace('/login');
    else if (deletion === null) router.replace('/');
  }, [user, deletion]); // eslint-disable-line react-hooks/exhaustive-deps

  const runCancel = () => {
    if (cancel.isPending) return;
    cancel.mutate(undefined, {
      onSuccess: (r) => {
        alert('Deletion cancelled', cancelledMessage(r));
        // The status query was invalidated; the effect above routes on.
      },
      onError: (err) => {
        if (err instanceof StepUpRequiredError) {
          setStepUp(true);
          return;
        }
        const { message, isCancelled } = handleError(err, { context: 'AccountDeletionPending.cancel' });
        if (isCancelled) return;
        alert(
          'Could not cancel',
          err instanceof AccountDeletionError ? err.userMessage : message,
        );
        if (err instanceof AccountDeletionError && (err.kind === 'erasure_in_progress' || err.kind === 'no_pending_request')) {
          status.refetch();
        }
      },
    });
  };

  const runSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch (err) {
      reportError(err, { context: 'AccountDeletionPending.signOut' });
    } finally {
      if (isWeb && typeof window !== 'undefined') window.location.replace('/login');
      else router.replace('/login');
    }
  };

  if (status.isError && deletion === undefined) {
    return <AuthGuardError onRetry={() => status.refetch()} />;
  }

  const pending = deletion?.status === 'pending';

  return (
    <ScreenFrame bottomEdge>
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: 20, paddingVertical: 32 }}
        showsVerticalScrollIndicator={false}
      >
        {!deletion ? (
          <View className="items-center justify-center" style={{ paddingVertical: 48 }}>
            <ActivityIndicator color={colors.primary[600]} />
          </View>
        ) : (
          <Animated.View
            entering={FadeInDown.duration(400)}
            style={{ width: '100%', maxWidth: 520, alignSelf: 'center', gap: 20 }}
          >
            {pending ? (
              <CountdownCard scheduledFor={deletion.scheduledFor} />
            ) : (
              <View style={{ gap: 12 }}>
                <View
                  className="items-center justify-center"
                  style={{ width: 56, height: 56, borderRadius: 18, backgroundColor: colors.red.light }}
                >
                  <Hourglass size={26} color={colors.error} />
                </View>
                <Text numberOfLines={3} style={{ color: colors.text.primary, fontSize: 26, fontWeight: '700', letterSpacing: -0.4 }}>
                  Your account is being deleted
                </Text>
                <Text numberOfLines={5} style={{ color: colors.text.secondary, fontSize: 15, lineHeight: 22 }}>
                  The deletion has started and can no longer be cancelled. We will email you when it is complete.
                </Text>
              </View>
            )}

            {pending && (
              <View
                style={{
                  borderRadius: 20,
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  overflow: 'hidden',
                }}
              >
                <Text
                  numberOfLines={1}
                  style={{
                    color: colors.text.tertiary,
                    fontSize: 12,
                    fontWeight: '700',
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    paddingHorizontal: 16,
                    paddingTop: 14,
                  }}
                >
                  Already in effect
                </Text>
                <EffectRow icon={<KeyRound size={18} color={colors.error} />} text="API keys and connected apps are revoked" />
                <EffectRow icon={<EyeOff size={18} color={colors.error} />} text="You are hidden from discovery and messages" />
                <EffectRow icon={<CreditCard size={18} color={colors.text.secondary} />} text="Any subscription is set not to renew" />
              </View>
            )}

            <View style={{ gap: 12 }}>
              {pending && (
                <AccountActionButton
                  variant="primary"
                  label={cancel.isPending ? 'Cancelling…' : 'Cancel deletion'}
                  loading={cancel.isPending}
                  onPress={runCancel}
                  accessibilityHint="Keeps your account and restores access"
                />
              )}
              <AccountActionButton
                variant="secondary"
                label="Sign out"
                loading={signingOut}
                onPress={runSignOut}
              />
            </View>
          </Animated.View>
        )}
      </ScrollView>

      <StepUpModal
        visible={stepUp}
        reason="cancel the deletion"
        onClose={() => setStepUp(false)}
        onVerified={() => {
          setStepUp(false);
          runCancel();
        }}
      />
    </ScreenFrame>
  );
}

function CountdownCard({ scheduledFor }: { scheduledFor: string }) {
  const colors = useThemeColors();
  const days = daysUntil(scheduledFor);
  return (
    <View style={{ gap: 16 }}>
      <View className="flex-row items-end" style={{ gap: 12 }}>
        <Text
          numberOfLines={1}
          className="shrink-0"
          style={{ color: colors.error, fontSize: 64, fontWeight: '800', letterSpacing: -2, fontVariant: ['tabular-nums'] }}
        >
          {days}
        </Text>
        <Text numberOfLines={2} className="flex-1" style={{ color: colors.text.secondary, fontSize: 16, fontWeight: '600', paddingBottom: 12 }}>
          {days === 1 ? 'day left' : 'days left'}
        </Text>
      </View>
      <Text numberOfLines={3} style={{ color: colors.text.primary, fontSize: 24, fontWeight: '700', letterSpacing: -0.4 }}>
        Your account is scheduled for deletion
      </Text>
      <View className="flex-row items-center" style={{ gap: 8 }}>
        <View className="shrink-0">
          <CalendarClock size={18} color={colors.text.secondary} />
        </View>
        <Text numberOfLines={2} className="flex-1" style={{ color: colors.text.secondary, fontSize: 15 }}>
          On {formatDeletionDate(scheduledFor)}. Cancel before then to keep it.
        </Text>
      </View>
    </View>
  );
}

function EffectRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  const colors = useThemeColors();
  return (
    <View className="flex-row items-center" style={{ gap: 12, paddingHorizontal: 16, paddingVertical: 12 }}>
      <View
        className="shrink-0 items-center justify-center"
        style={{ width: 32, height: 32, borderRadius: 9, backgroundColor: colors.surface.secondary }}
      >
        {icon}
      </View>
      <Text numberOfLines={2} className="flex-1" style={{ color: colors.text.primary, fontSize: 14 }}>
        {text}
      </Text>
    </View>
  );
}
